import type { PublicCourse } from '../../../services/publicService';
import { isFreePracticeLab } from './content';

/**
 * The six free NSC papers, read off the live catalogue and grouped for /free.
 *
 * DERIVED, not listed. A static manifest of the six would be a second place
 * they live — the same trap `LabSequence` documents for the worked example —
 * and this one would rot the day a seventh paper is published or a title is
 * corrected. The catalogue is the source; this module only reads it.
 *
 * What makes that safe is that the titles are structured, not prose:
 *
 *   Practice Lab: Paper 1 | Q1 | Origins of the Cold War
 *
 * and the same `isFreePracticeLab` prefix that gates the landing page's browse
 * window gates this list, so the two can never disagree about which courses
 * are "the free papers".
 *
 * The year is the one field the title does NOT carry — it is in the
 * description ("The official NSC November 2024 Paper 1 Source-based
 * question…"). That is a softer contract than the title, so a miss drops the
 * year and keeps the row rather than failing the parse.
 */

/** `Practice Lab: Paper 1 | Q1 | Origins of the Cold War` */
const TITLE = /^Practice Lab:\s*Paper\s*(\d+)\s*\|\s*(Q[\d.]+)\s*\|\s*(.+)$/i;
/** `…the official NSC November 2024 Paper 1 Source-based question…` */
const YEAR = /\bNovember\s+(\d{4})\b/i;

export interface FreePaper {
  id: string;
  /** Paper number, or null when the title doesn't follow the shape above. */
  paper: number | null;
  /** `Q1`, or null for the same reason. */
  q: string | null;
  /** The topic alone on a parsed title; the whole title otherwise. */
  topic: string;
  year: string | null;
}

export interface FreePaperGroup {
  /** `Paper 1`, or null for the trailing group of unparsed rows. */
  label: string | null;
  papers: FreePaper[];
}

export const parseFreePaper = (course: Pick<PublicCourse, 'id' | 'title' | 'description'>): FreePaper => {
  const m = TITLE.exec(course.title.trim());
  const year = YEAR.exec(course.description ?? '')?.[1] ?? null;
  if (!m) {
    /* An unrecognised title is still a real, free, published paper — showing
       it untidily beats hiding it. It sorts to the end and gets no question
       number rather than a guessed one. */
    return { id: course.id, paper: null, q: null, topic: course.title.trim(), year };
  }
  return { id: course.id, paper: Number(m[1]), q: m[2].toUpperCase(), topic: m[3].trim(), year };
};

/**
 * Every free paper in the catalogue, grouped by paper and ordered by question.
 *
 * Explicitly ordered rather than left to the catalogue's own sort, which is
 * `total_students` descending: on the live site that returns Q3, Q2, Q1 and
 * reads as shuffled, and it would re-order itself as students enrolled.
 */
export const groupFreePapers = (courses: PublicCourse[]): FreePaperGroup[] => {
  const papers = courses.filter((c) => isFreePracticeLab(c.title)).map(parseFreePaper);

  const byPaper = new Map<number, FreePaper[]>();
  const loose: FreePaper[] = [];
  for (const p of papers) {
    if (p.paper === null) {
      loose.push(p);
      continue;
    }
    const bucket = byPaper.get(p.paper);
    if (bucket) bucket.push(p);
    else byPaper.set(p.paper, [p]);
  }

  // `numeric` so Q10 sorts after Q9 rather than between Q1 and Q2.
  const byQuestion = (a: FreePaper, b: FreePaper) =>
    (a.q ?? '').localeCompare(b.q ?? '', undefined, { numeric: true });

  const groups: FreePaperGroup[] = [...byPaper.entries()]
    .sort(([a], [b]) => a - b)
    .map(([n, rows]) => ({ label: `Paper ${n}`, papers: [...rows].sort(byQuestion) }));

  if (loose.length > 0) {
    groups.push({
      label: null,
      papers: [...loose].sort((a, b) => a.topic.localeCompare(b.topic)),
    });
  }
  return groups;
};
